export type SihcRuntimeUrls = {
  urlBase?: string;
  apiBaseUrl?: string;
  apiDocumentUrl?: string;
  apiRapportsBaseUrl?: string;
  viewRapportsBaseUrl?: string;
  cloudflare?: { url?: string };
  dev?: { serverUrl?: string };
};

declare global {
  interface Window {
    __SIHC_CONFIG__?: SihcRuntimeUrls;
  }
}

const URL_KEYS = [
  'urlBase',
  'apiBaseUrl',
  'apiDocumentUrl',
  'apiRapportsBaseUrl',
  'viewRapportsBaseUrl',
] as const;

function currentOrigin(): string | undefined {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return undefined;
  }
  return window.location.origin.replace(/\/$/, '');
}

/**
 * Path of a configured URL. Hostnames from env/config are discarded so the
 * browser origin (`window.location`) is always used.
 */
export function pathFromConfiguredUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '/';
  }

  try {
    const absolute = new URL(trimmed);
    if (absolute.protocol === 'http:' || absolute.protocol === 'https:') {
      return ensureTrailingSlash(absolute.pathname || '/');
    }
  } catch {
    // relative path
  }

  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return ensureTrailingSlash(path);
}

function ensureTrailingSlash(path: string): string {
  return path.endsWith('/') ? path : `${path}/`;
}

/**
 * Rebuilds a SIHC public URL against `window.location.origin`. Relative paths
 * and leftover absolute hosts (env vars, compiled env files) all become
 * same-origin.
 */
export function toSameOriginUrl(pathOrUrl: string): string {
  const path = pathFromConfiguredUrl(pathOrUrl);
  const origin = currentOrigin();
  return origin ? `${origin}${path}` : path;
}

function isExternalAbsoluteUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return false;
    }
    const host = url.hostname.toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') {
      return false;
    }
    if (host === 'sihc.hamelconstruction.com' || host.endsWith('.sihc.hamelconstruction.com')) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function rewriteOwnedUrl(value: string): string {
  if (isExternalAbsoluteUrl(value)) {
    return ensureTrailingSlash(value.trim());
  }
  return toSameOriginUrl(value);
}

/**
 * Overlay optional `/config.js` path overrides, then force every SIHC public
 * URL onto the current `window.location` origin.
 */
export function applyRuntimeConfig<T extends SihcRuntimeUrls>(base: T): T {
  const raw = typeof window === 'undefined' ? undefined : window.__SIHC_CONFIG__;
  const overlay: SihcRuntimeUrls = {};
  if (raw && typeof raw === 'object') {
    for (const key of URL_KEYS) {
      const value = raw[key];
      if (typeof value === 'string' && value.trim()) {
        overlay[key] = value.trim();
      }
    }
  }

  const merged = { ...base, ...overlay } as T & SihcRuntimeUrls;

  if (typeof merged.urlBase === 'string') {
    merged.urlBase = toSameOriginUrl(merged.urlBase);
  }
  if (typeof merged.apiBaseUrl === 'string') {
    merged.apiBaseUrl = toSameOriginUrl(merged.apiBaseUrl);
  }
  if (typeof merged.apiDocumentUrl === 'string') {
    merged.apiDocumentUrl = toSameOriginUrl(merged.apiDocumentUrl);
  }
  if (typeof merged.apiRapportsBaseUrl === 'string') {
    merged.apiRapportsBaseUrl = toSameOriginUrl(merged.apiRapportsBaseUrl);
  }
  if (typeof merged.viewRapportsBaseUrl === 'string') {
    merged.viewRapportsBaseUrl = toSameOriginUrl(merged.viewRapportsBaseUrl);
  }
  if (merged.cloudflare && typeof merged.cloudflare.url === 'string') {
    merged.cloudflare = { ...merged.cloudflare, url: rewriteOwnedUrl(merged.cloudflare.url) };
  }
  if (merged.dev && typeof merged.dev.serverUrl === 'string') {
    merged.dev = { ...merged.dev, serverUrl: toSameOriginUrl(merged.dev.serverUrl) };
  }

  return merged;
}
